/** Author: Codex CLI Agent // Generation: Winner overlay // Version: v3.2.272 // Modified: 2025-12-04T00:20:00+08:00 // One-line: GAME_ENDED 胜者信息卡（头像/颜色/名字/击杀） */
import React, { useEffect, useMemo, useState } from 'react';
import webSocketService from '../services/websocket/WebSocketService';
import { usePortraitPreloader } from '../hooks/usePortraitPreloader';
import { colorToIndex, indexToEmoji, normalizeColor } from '../utils/colorMapping';

type AgentInfo = {
  id: string;
  name?: string;
  colorIndex?: number;
};

const overlayStyle: React.CSSProperties = {
  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50
};

const panelStyle: React.CSSProperties = {
  background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(34,197,94,0.5)', borderRadius: 12,
  padding: 24, color: '#e2e8f0', minWidth: 420, textAlign: 'center', maxWidth: 720
};

const gridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 12 };

const cardStyle: React.CSSProperties = {
  background: 'rgba(2,6,23,0.7)', border: '1px solid rgba(59,130,246,0.35)', borderRadius: 10, padding: 12
};

export default function WinnerBanner(): JSX.Element | null {
  const [winners, setWinners] = useState<string[] | null>(null);
  const [agents, setAgents] = useState<Map<string, AgentInfo>>(new Map());
  const [kills, setKills] = useState<Record<string, number>>({});
  const { portraits } = usePortraitPreloader();

  // 订阅快照，收集 agent 的 name/color
  useEffect(() => {
    const onSnap = (snap?: any) => {
      try {
        const list = Array.isArray(snap?.agents) ? snap.agents : [];
        if (!list.length) return;
        setAgents((prev) => {
          const next = new Map(prev);
          list.forEach((a: any) => {
            const id = String(a?.id || a?.agentId || ''); if (!id) return;
            let idx = 0;
            if (typeof a?.characterType === 'number') idx = Math.max(0, Math.min(6, (a.characterType|0)));
            else idx = colorToIndex(a?.color || a?.slimeType);
            next.set(id, { id, name: a?.name || id, colorIndex: idx });
          });
          return next;
        });
      } catch {}
    };
    try { webSocketService.on('SYSTEM_STATE_SYNC', onSnap); } catch {}
    return () => { try { webSocketService.off('SYSTEM_STATE_SYNC', onSnap); } catch {} };
  }, []);

  // 统计战斗击杀数（基于 COMBAT_ENDED 的 winner 字段）
  useEffect(() => {
    const onCombatEnd = (d: any) => {
      try {
        const w = String(d?.winner || ''); if (!w) return;
        setKills((prev) => ({ ...prev, [w]: (prev[w] || 0) + 1 }));
      } catch {}
    };
    try { webSocketService.on('COMBAT_ENDED', onCombatEnd); } catch {}
    return () => { try { webSocketService.off('COMBAT_ENDED', onCombatEnd); } catch {} };
  }, []);

  // 只在整局结束时展示
  useEffect(() => {
    const onEnded = (d: any) => {
      try {
        const list = Array.isArray(d?.winners) ? d.winners : (d?.winner ? [d.winner] : []);
        if (list && list.length) setWinners(list.map(String));
      } catch {}
    };
    try { webSocketService.on('GAME_ENDED', onEnded); } catch {}
    return () => { try { webSocketService.off('GAME_ENDED', onEnded); } catch {} };
  }, []);

  const winnerCards = useMemo(() => {
    if (!winners || !winners.length) return [] as Array<{ id: string; name: string; colorIndex: number; portrait?: string; k: number }>;
    return winners.map((id) => {
      const ai = agents.get(id);
      const name = ai?.name || id;
      const colorIndex = typeof ai?.colorIndex === 'number' ? ai!.colorIndex! : colorToIndex('green');
      const portrait = portraits[colorIndex];
      const k = kills[id] || 0;
      return { id, name, colorIndex, portrait, k };
    });
  }, [winners, agents, portraits, kills]);

  if (!winners || winners.length === 0) return null;

  return (
    <div style={overlayStyle} data-testid="winner-overlay">
      <div style={panelStyle}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>🏁 游戏结束</div>
        <div style={{ fontSize: 16, opacity: 0.85, marginBottom: 14 }}>🏆 胜者</div>
        <div style={gridStyle}>
          {winnerCards.map((w) => (
            <div key={w.id} style={cardStyle}>
              {w.portrait ? (
                <img src={w.portrait} alt="portrait" style={{ width: 96, height: 96, imageRendering: 'pixelated', borderRadius: 8, border: '1px solid rgba(148,163,184,0.35)' }} />
              ) : (
                <div style={{ width: 96, height: 96, borderRadius: 8, background: 'rgba(2,6,23,0.5)', border: '1px solid rgba(148,163,184,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                  <span style={{ fontSize: 28 }}>{indexToEmoji(w.colorIndex)}</span>
                </div>
              )}
              <div style={{ marginTop: 8, fontSize: 16, fontWeight: 600 }}>{w.name}</div>
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.85 }}>🗡️ 击杀：{w.k}</div>
              <div style={{ marginTop: 2, fontSize: 12, opacity: 0.75 }}>颜色：{indexToEmoji(w.colorIndex)}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, opacity: 0.8, marginTop: 12 }}>感谢观战</div>
      </div>
    </div>
  );
}
